import { Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { ModalsActions } from '../../../../../core/modals/actions/modals.actions';
import { BaseAppState } from '../../../../../core/store/reducers';
import { User, UserFlow } from '../../../../../shared/models';
import {
  getBMI,
  getBMIScore,
  getWeightChangeScore,
} from '../../../../../shared/utilities/users-utils';
import { UserDataSelectors } from '../../../../../core/users/selectors/user-data.selectors';
import { UsersSelectors } from '../../../../../core/users/selectors/users-selectors';
import { combineLatest, BehaviorSubject } from 'rxjs';
import { take, takeUntil, map } from 'rxjs/operators';
import { ObservableComponent } from 'ngx-esprio-shared';

@Component({
  selector: 'app-monitored-user-flow',
  templateUrl: './monitored-user-flow.component.html',
  styleUrls: ['./monitored-user-flow.component.scss'],
})
export class MonitoredUserFlowComponent extends ObservableComponent implements OnInit {
  @Input() public monitoredUser: User;

  private monitoredUser$ = new BehaviorSubject<User | undefined>(undefined);
  public selectedFlow: UserFlow;
  public UserFlow = UserFlow;

  public latestSubmissionDetails$ = this.store.select(UserDataSelectors.getLatestSubmissionDetails);

  public bmi$ = combineLatest([
    this.monitoredUser$,
    this.store.select(UserDataSelectors.getScoreDetails),
  ]).pipe(
    map(([user, score]) => {
      const fromScore = score?.bmi;
      if (typeof fromScore === 'number' && !isNaN(fromScore) && fromScore > 0) return fromScore;
      return user
        ? getBMI(user.coreData?.bodySizeInCm, user.coreData?.weightSixMonthsAgoInKg)
        : null;
    })
  );

  public bmiScore$ = this.bmi$.pipe(map((bmi) => (bmi === null ? null : getBMIScore(bmi))));

  public weightChange$ = combineLatest([
    this.store.select(UserDataSelectors.getScoreDetails),
    this.store.select(UserDataSelectors.getWeightChangePercentage),
  ]).pipe(
    map(([details, legacy]) => {
      if (typeof details?.weightChangePercentage === 'number')
        return details.weightChangePercentage;
      const direct = legacy?.weightChangePercentage;
      return typeof direct === 'number' ? direct : null;
    })
  );

  public weightChangeScore$ = this.weightChange$.pipe(
    map((change) => (change === null ? null : getWeightChangeScore(change)))
  );

  public foodIntakeScore$ = this.latestSubmissionDetails$.pipe(
    map((details): number | null => {
      const index = details?.questions?.[2]?.answers?.[0];
      if (typeof index !== 'number') return null;
      if (index === 0) return 0;
      if (index === 1) return 1;
      if (index === 2) return 2;
      return null;
    })
  );

  public question1Score$ = this.latestSubmissionDetails$.pipe(
    map((details): number | null => {
      const value = details?.questions?.[0]?.answers?.[0];
      if (typeof value !== 'number') return null;
      if (value < 4) return 0;
      if (value <= 6) return 1;
      if (value >= 7) return 2;
      return null;
    })
  );

  public question2Score$ = this.latestSubmissionDetails$.pipe(
    map((details): number | null => {
      const value = details?.questions?.[1]?.answers?.[0];
      if (typeof value !== 'number') return null;
      if (value < 4) return 0;
      if (value <= 6) return 1;
      if (value >= 7) return 2;
      return null;
    })
  );

  public mustScore$ = this.store
    .select(UserDataSelectors.getScoreDetails)
    .pipe(map((d) => (typeof d?.mustScore === 'number' ? d.mustScore : null)));

  public strasserScore$ = this.store
    .select(UserDataSelectors.getScoreDetails)
    .pipe(map((d) => (typeof d?.strasserScore === 'number' ? d.strasserScore : null)));

  public finalAssignmentScore$ = combineLatest([this.mustScore$, this.strasserScore$]).pipe(
    map(([mustScore, strasserScore]) => {
      if (mustScore === null && strasserScore === null) return null;
      if (mustScore === null) return strasserScore;
      if (strasserScore === null) return mustScore;
      return Math.max(mustScore, strasserScore);
    })
  );

  public vm$ = combineLatest([
    this.monitoredUser$,
    this.bmi$,
    this.bmiScore$,
    this.weightChange$,
    this.weightChangeScore$,
    this.latestSubmissionDetails$,
    this.foodIntakeScore$,
    this.question1Score$,
    this.question2Score$,
    this.mustScore$,
    this.strasserScore$,
    this.finalAssignmentScore$,
  ]).pipe(
    map(
      ([
        monitoredUser,
        bmi,
        bmiScore,
        weightChange,
        weightChangeScore,
        latestSubmissionDetails,
        foodIntakeScore,
        question1Score,
        question2Score,
        mustScore,
        strasserScore,
        finalAssignmentScore,
      ]) => ({
        monitoredUser,
        bmi,
        bmiScore,
        weightChange,
        weightChangeScore,
        latestSubmissionDetails,
        foodIntakeScore,
        question1Score,
        question2Score,
        mustScore,
        strasserScore,
        finalAssignmentScore,
      })
    )
  );

  currentIcons = {
    [UserFlow.Red]: 'assets/images/red-flow-icon.svg',
    [UserFlow.Yellow]: 'assets/images/yellow-flow-icon.svg',
    [UserFlow.Green]: 'assets/images/green-flow-icon.svg',
  };

  whiteFlowIcon = 'assets/images/white-flow-icon.svg';

  constructor(private store: Store<BaseAppState>) {
    super();
  }

  ngOnInit(): void {
    if (this.monitoredUser.flow) {
      this.selectedFlow = this.monitoredUser.flow;
    }
    this.monitoredUser$.next(this.monitoredUser);

    combineLatest([
      this.store.select(UsersSelectors.getSelectedUserId),
      this.store.select(UsersSelectors.getSelectedUserId),
    ])
      .pipe(take(1), takeUntil(this.ngDestroy$))
      .subscribe(() => {});
  }

  public handleChangeFlow(userId: string) {
    this.store.dispatch(
      ModalsActions.openConfirmChangeFlowModal({
        request: { monitoredUserId: userId, selectedFlow: this.selectedFlow },
      })
    );
  }

  public updateSelectedFlow(userFlow: UserFlow) {
    this.selectedFlow = userFlow;
  }

  public changeIcon(iconSrc: string, userFlow: UserFlow) {
    this.currentIcons[userFlow] = iconSrc;
  }
}
